// import
import Dashboard from "views/Dashboard/Dashboard";
import MapData from "views/Dashboard/MapData";
import Users from "views/Dashboard/Users";
import Invitations from "views/Dashboard/Invitations";
import Profile from "views/Dashboard/Profile";
import SignIn from "views/Auth/SignIn.js";
import RequestPasswordReset from "views/Auth/RequestPasswordReset";
import ResetPassword from "views/Auth/ResetPassword";
import AcceptInvitation from "views/Auth/AcceptInvitation.js";

import {
  HomeIcon,
  StatsIcon,
  PersonIcon,
  DocumentIcon,
  RocketIcon,
} from "components/Icons/Icons";
import Individuals from "views/Dashboard/Individuals";
import Individual from "views/Dashboard/Individual";
import AddIndividualForm from "views/Dashboard/IndividualADD";
import FamilyAdd from "views/Dashboard/FamilyAdd";

import Families from "views/Dashboard/Families";
import EditFamily from "views/Dashboard/EditFamily";
import FamilyMembers from "views/Dashboard/FamilyMembers";
import EditIndividual from "views/Dashboard/EditIndividual";
import { getRole, isTokenValid, canManageData, isAdmin } from "utils/auth";
import Forums from "views/Dashboard/Forums";
import PostDetails from "views/Dashboard/PostDetails";
import CreatePost from "views/Dashboard/CreatePost";
import Statistics from "views/Dashboard/Statistics";
import Backup from "views/Dashboard/Backup";
import Logs from "views/Dashboard/Logs";
import {
  FaAddressCard,
  FaInfo,
  FaMapMarkedAlt,
  FaRegCommentAlt,
  FaSitemap,
  FaUsers,
  FaUserPlus,
  FaDatabase,
  FaHistory,
} from "react-icons/fa";
import Information from "views/Dashboard/Infos";
import Home from "views/Dashboard/Documentation/Home";
import NormalUserDoc from "views/Dashboard/Documentation/NormalUserDoc";
import AdminDoc from "views/Dashboard/Documentation/AdminDoc";

// Public navigation items accessible by unauthenticated visitors
export const getPublicRoutes = () => [
  {
    path: "/mapdata",
    name: "Map",
    icon: <FaMapMarkedAlt color="inherit" />,
    component: MapData,
    layout: "/txd",
    isPublic: true,
  },
  {
    path: "/stats",
    name: "XP in numbers",
    icon: <StatsIcon color="inherit" />,
    component: Statistics,
    layout: "/txd",
    isPublic: true,
  },
  {
    path: "/info",
    name: "Bibliography and useful links",
    icon: <FaInfo color="inherit" />,
    component: Information,
    layout: "/txd",
    isPublic: true,
  },
  {
    path: "/doc",
    name: "User guide",
    icon: <FaSitemap color="inherit" />,
    component: Home,
    layout: "/txd",
    isPublic: true,
    children: [
      {
        path: "/user",
        component: NormalUserDoc,
        layout: "/txd",
        isPublic: true,
      },
      {
        path: "/admin",
        component: AdminDoc,
        layout: "/txd",
        isPublic: true,
      },
    ],
  },
];

// Complete set of all available routes used by React Router in Admin.js
export const getAllDashRoutes = () => {
  return [
    {
      path: "/dashboard",
      name: "Home",
      icon: <HomeIcon color="inherit" />,
      component: Dashboard,
      layout: "/txd",
    },
    {
      path: "/individuals",
      name: "Individuals",
      icon: <FaAddressCard color="inherit" />,
      component: Individuals,
      layout: "/txd",
      children: [
        {
          path: "/info/:individual_id",
          component: Individual,
          layout: "/txd",
        },
        {
          path: "/edit/:individual_id",
          component: EditIndividual,
          layout: "/txd",
          requiresManagement: true,
        },
        {
          path: "/add",
          component: AddIndividualForm,
          layout: "/txd",
          requiresManagement: true,
        },
      ],
    },
    {
      path: "/families",
      name: "Families",
      icon: <FaSitemap color="inherit" />,
      component: Families,
      layout: "/txd",
      children: [
        {
          path: "/edit/:familyCode",
          component: EditFamily,
          layout: "/txd",
          requiresManagement: true,
        },
        {
          path: "/members/:familyCode",
          component: FamilyMembers,
          layout: "/txd",
        },
        {
          path: "/add",
          component: FamilyAdd,
          layout: "/txd",
          requiresManagement: true,
        },
      ],
    },
    {
      path: "/forums",
      name: "Forums",
      icon: <FaRegCommentAlt color="inherit" />,
      component: Forums,
      layout: "/txd",
      children: [
        {
          path: "/post/:id",
          component: PostDetails,
          layout: "/txd",
        },
        {
          path: "/create",
          component: CreatePost,
          layout: "/txd",
        },
      ],
    },
    ...getPublicRoutes(),
    {
      name: "ADMIN PAGES",
      category: "account",
      state: "pageCollapse",
      requiresManagement: true,
      views: [
        {
          path: "/users",
          name: "Users",
          icon: <FaUsers color="inherit" />,
          component: Users,
          layout: "/txd",
          requiresManagement: true,
        },
        {
          path: "/invitations",
          name: "Invitations",
          icon: <FaUserPlus color="inherit" />,
          component: Invitations,
          layout: "/txd",
          requiresManagement: true,
        },
        {
          path: "/backup",
          name: "Backup & Restore",
          icon: <FaDatabase color="inherit" />,
          component: Backup,
          layout: "/txd",
          requiresAdmin: true,
        },
        {
          path: "/logs",
          name: "Audit Logs",
          icon: <FaHistory color="inherit" />,
          component: Logs,
          layout: "/txd",
          requiresAdmin: true,
        },
      ],
    },
    {
      name: "ACCOUNT PAGES",
      category: "account",
      state: "pageCollapse",
      views: [
        {
          path: "/profile",
          name: "Profile",
          icon: <PersonIcon color="inherit" />,
          secondaryNavbar: true,
          component: Profile,
          layout: "/txd",
        },
        {
          path: "/signin",
          name: "Sign In",
          icon: <DocumentIcon color="inherit" />,
          component: SignIn,
          layout: "/auth",
        },
        {
          path: "/request-password-reset",
          name: "Request Reset Password",
          icon: <DocumentIcon color="inherit" />,
          component: RequestPasswordReset,
          layout: "/auth",
        },
        {
          path: "/reset-password/:token",
          name: "Reset Password",
          icon: <DocumentIcon color="inherit" />,
          component: ResetPassword,
          layout: "/auth",
        },
        {
          path: "/accept/:code",
          name: "Accept Invitation",
          icon: <RocketIcon color="inherit" />,
          secondaryNavbar: true,
          component: AcceptInvitation,
          layout: "/auth",
        },
      ],
    },
  ];
};

// Sidebar routes filtered dynamically by authentication state and role
export const getSidebarRoutes = () => {
  const authenticated = isTokenValid();

  // Public visitor: only public navigation
  if (!authenticated) {
    return getPublicRoutes();
  }

  // Logged-in user base navigation
  const routes = [
    {
      path: "/dashboard",
      name: "Home",
      icon: <HomeIcon color="inherit" />,
      component: Dashboard,
      layout: "/txd",
    },
    {
      path: "/individuals",
      name: "Individuals",
      icon: <FaAddressCard color="inherit" />,
      component: Individuals,
      layout: "/txd",
    },
    {
      path: "/families",
      name: "Families",
      icon: <FaSitemap color="inherit" />,
      component: Families,
      layout: "/txd",
    },
    {
      path: "/forums",
      name: "Forums",
      icon: <FaRegCommentAlt color="inherit" />,
      component: Forums,
      layout: "/txd",
    },
    {
      path: "/mapdata",
      name: "Map",
      icon: <FaMapMarkedAlt color="inherit" />,
      component: MapData,
      layout: "/txd",
    },
    {
      path: "/stats",
      name: "XP in numbers",
      icon: <StatsIcon color="inherit" />,
      component: Statistics,
      layout: "/txd",
    },
    {
      path: "/info",
      name: "Bibliography and useful links",
      icon: <FaInfo color="inherit" />,
      component: Information,
      layout: "/txd",
    },
    {
      path: "/doc",
      name: "User guide",
      icon: <FaSitemap color="inherit" />,
      component: Home,
      layout: "/txd",
    },
  ];

  // If admin or moderator: add ADMIN PAGES (Users, Invitations, Backup)
  if (canManageData()) {
    const adminViews = [
      {
        path: "/users",
        name: "Users",
        icon: <FaUsers color="inherit" />,
        component: Users,
        layout: "/txd",
      },
      {
        path: "/invitations",
        name: "Invitations",
        icon: <FaUserPlus color="inherit" />,
        component: Invitations,
        layout: "/txd",
      },
    ];

    if (isAdmin()) {
      adminViews.push({
        path: "/backup",
        name: "Backup & Restore",
        icon: <FaDatabase color="inherit" />,
        component: Backup,
        layout: "/txd",
      });
      adminViews.push({
        path: "/logs",
        name: "Audit Logs",
        icon: <FaHistory color="inherit" />,
        component: Logs,
        layout: "/txd",
      });
    }

    routes.push({
      name: "ADMIN PAGES",
      category: "account",
      state: "pageCollapse",
      views: adminViews,
    });
  }

  // Account Profile for logged in users
  routes.push({
    name: "ACCOUNT PAGES",
    category: "account",
    state: "pageCollapse",
    views: [
      {
        path: "/profile",
        name: "Profile",
        icon: <PersonIcon color="inherit" />,
        secondaryNavbar: true,
        component: Profile,
        layout: "/txd",
      },
    ],
  });

  return routes;
};

export const getDashRoutes = getAllDashRoutes;
export default getAllDashRoutes;
